import type { TransactionFilters } from "~/lib/queries";

/**
 * Every filter on the transactions list, as URL search params.
 *
 * The URL is the only place these live. A filtered, paged view is therefore a
 * link: paste it into a thread and the person who opens it sees the same table,
 * which is most of what makes this screen useful during an incident.
 *
 * `deleted` rather than `is_deleted` because it is a UI toggle, not the API
 * field -- absent means live rows, and the mapping to the API's tri-state
 * happens in one place below.
 */
export interface TransactionSearch {
  storefront_id?: string;
  payment_status?: string;
  payment_method?: string;
  payment_provider?: string;
  order_id?: string;
  source?: string;
  start_date?: string;
  end_date?: string;
  deleted?: boolean;
  limit?: number;
  offset?: number;
}

/** Keys that filter the result set, as opposed to paging through it. */
const FILTER_KEYS = [
  "storefront_id",
  "payment_status",
  "payment_method",
  "payment_provider",
  "order_id",
  "source",
  "start_date",
  "end_date",
  "deleted",
] as const satisfies readonly (keyof TransactionSearch)[];

/**
 * Reads search params off the URL.
 *
 * Anything unrecognised or empty becomes undefined, which TanStack Router then
 * drops from the URL entirely -- so a cleared filter leaves no `?source=`
 * behind, and a hand-edited URL cannot put a filter into a state the controls
 * cannot represent.
 */
export function validateTransactionSearch(raw: Record<string, unknown>): TransactionSearch {
  return {
    storefront_id: str(raw.storefront_id),
    payment_status: str(raw.payment_status),
    payment_method: str(raw.payment_method),
    payment_provider: str(raw.payment_provider),
    order_id: str(raw.order_id),
    source: str(raw.source),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    deleted: bool(raw.deleted),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

/** True when the result set is narrowed, ignoring paging. */
export function hasActiveFilters(search: TransactionSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

/**
 * Maps the URL's shape onto the API's.
 *
 * The one interesting case is `deleted`: the server treats a missing is_deleted
 * as "live rows only", not "either", so the flag is sent only when it is on.
 */
export function toFilters(search: TransactionSearch): TransactionFilters {
  return {
    storefront_id: search.storefront_id,
    payment_status: search.payment_status,
    payment_method: search.payment_method,
    payment_provider: search.payment_provider,
    order_id: search.order_id,
    source: search.source,
    start_date: search.start_date,
    end_date: search.end_date,
    is_deleted: search.deleted ? true : undefined,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function bool(value: unknown): boolean | undefined {
  if (value === true || value === "true") return true;
  return undefined;
}
